// MessageDetail.jsx
import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "./api";
import "../styles/messagesdetail.css";

const MessagesDetail = () => {
  const { id } = useParams();
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [hoveredMessageId, setHoveredMessageId] = useState(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  const [error, setError] = useState("");
  const [showError, setShowError] = useState(false);
  const [errorTimer, setErrorTimer] = useState(null);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messageRefs = useRef({});
  const navigate = useNavigate();
  const isInitialMount = useRef(true);

  const goBack = () => {
    navigate(-1);
  };

  const extractErrorMessage = (error) => {
    if (error.response) {
      const data = error.response.data;
      
      if (typeof data === 'string') {
        return data;
      }
      
      if (typeof data === 'object') {
        if (data.message) return data.message;
        if (data.error) return data.error;
        if (data.detail) return data.detail;
        if (data.error_message) return data.error_message;
        
        if (typeof data === 'object' && !Array.isArray(data)) {
          const errorMessages = [];
          for (const key in data) {
            if (Array.isArray(data[key])) {
              errorMessages.push(`${key}: ${data[key].join(', ')}`);
            } else if (typeof data[key] === 'string') {
              errorMessages.push(`${key}: ${data[key]}`);
            } else if (typeof data[key] === 'object') {
              errorMessages.push(`${key}: ${JSON.stringify(data[key])}`);
            }
          }
          if (errorMessages.length > 0) {
            return errorMessages.join(' | ');
          }
        }
        
        return JSON.stringify(data);
      }
    } else if (error.request) {
      return "ارتباط با سرور برقرار نشد. لطفاً اتصال اینترنت خود را بررسی کنید.";
    } else {
      return error.message || "خطای ناشناخته رخ داده است.";
    }
    
    return "خطا در ارتباط با سرور. لطفاً دوباره تلاش کنید.";
  };

  const showErrorMessage = (message) => {
    if (errorTimer) {
      clearTimeout(errorTimer);
      setErrorTimer(null);
    }
    
    setError(message);
    setShowError(true);
    
    const timer = setTimeout(() => {
      setShowError(false);
      setTimeout(() => {
        setError("");
      }, 500);
    }, 4000);
    
    setErrorTimer(timer);
  };

  const clearError = () => {
    if (errorTimer) {
      clearTimeout(errorTimer);
      setErrorTimer(null);
    }
    setShowError(false);
    setTimeout(() => {
      setError("");
    }, 500);
  };

  // ========== تابع ایجاد تیکت جدید با قابلیت ارسال پیام اول ==========
  const createNewTicket = async (packageId, initialMessage = null, initialFile = null) => {
    try {
      const formData = new FormData();
      
      if (initialMessage && initialMessage.trim()) {
        formData.append('message', initialMessage.trim());
      }
      
      if (initialFile) {
        formData.append('attachment', initialFile);
      }

      // اگر هیچ داده‌ای وجود نداشت، یک درخواست ساده بدون body بفرست
      const hasData = (initialMessage && initialMessage.trim()) || initialFile;
      
      const response = await api.post(
        `/pre_student/api/v3/tickets/get-or-create/${packageId}/`,
        hasData ? formData : {}
      );
      
      if (response.status === 200 || response.status === 201) {
        const data = response.data;
        
        if (data.ticket_id) {
          return data.ticket_id;
        }
        if (data.id) {
          return data.id;
        }
        return null;
      }
      return null;
    } catch (error) {
      console.error("Error creating ticket:", error);
      const errorMessage = extractErrorMessage(error);
      showErrorMessage(`خطا در ایجاد تیکت: ${errorMessage}`);
      return null;
    }
  };

  // ========== تابع دریافت جزئیات تیکت ==========
  const fetchTicketDetail = async (ticketId) => {
    try {
      const response = await api.get(`/pre_student/api/v1/tickets-detail/${ticketId}/`);
      
      setTicket({
        ticket_id: response.data.ticket_id || ticketId,
        package_title: response.data.package_title || "",
        status: response.data.status || "open",
      });
      
      const msgs = response.data.messages || [];
      
      const sortedMsgs = [...msgs].sort(
        (a, b) => new Date(a.created_at) - new Date(b.created_at)
      );
      setMessages(sortedMsgs);
    } catch (error) {
      console.error("خطا در دریافت جزئیات تیکت:", error);
      const errorMessage = extractErrorMessage(error);
      showErrorMessage(`خطا در دریافت پیام‌ها: ${errorMessage}`);
      throw error;
    }
  };

  // ========== useEffect اصلی ==========
  useEffect(() => {
    const initializeTicket = async () => {
      setLoading(true);
      clearError();
      
      try {
        // ابتدا سعی می‌کنیم با id به عنوان ticket_id دریافت کنیم
        try {
          await fetchTicketDetail(id);
          setLoading(false);
          return;
        } catch (error) {
          // اگر خطا 404 بود، یعنی تیکت وجود ندارد و باید جدید ایجاد کنیم
          if (error.response?.status === 404) {
            console.log("تیکت وجود ندارد، ایجاد تیکت جدید...");
            // id در اینجا package_id است
            const newTicketId = await createNewTicket(id);
            if (newTicketId) {
              // بعد از ایجاد تیکت، دوباره دریافت می‌کنیم
              await fetchTicketDetail(newTicketId);
              // به روز رسانی URL بدون رفرش صفحه
              window.history.replaceState(null, '', `/messages-detail/${newTicketId}`);
            } else {
              showErrorMessage("خطا در ایجاد تیکت. لطفاً دوباره تلاش کنید.");
            }
          } else {
            // خطای دیگر
            throw error;
          }
        }
      } catch (error) {
        console.error("خطا در مقداردهی تیکت:", error);
        showErrorMessage("خطا در بارگذاری تیکت. لطفاً دوباره تلاش کنید.");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      initializeTicket();
    }
    
    return () => {
      if (errorTimer) {
        clearTimeout(errorTimer);
      }
    };
  }, [id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (error || showError) {
        setError("");
        setShowError(false);
        if (errorTimer) {
          clearTimeout(errorTimer);
          setErrorTimer(null);
        }
      }
    }
  }, []);

  const scrollToMessage = (messageId) => {
    if (messageRefs.current[messageId]) {
      messageRefs.current[messageId].scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      
      setHighlightedMessageId(messageId);
      
      setTimeout(() => {
        setHighlightedMessageId(null);
      }, 3000);
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString("fa-IR", { hour: "2-digit", minute: "2-digit" });
    } catch {
      return "";
    }
  };

  const getRepliedMessage = (replyToId) => {
    return messages.find(msg => msg.id === replyToId);
  };

  const handleReply = (msg) => {
    if (!msg) return;
    setReplyTo({
      id: msg.id,
      message: msg.message === " " || !msg.message ? "[فایل پیوست]" : msg.message,
      sender: msg.sender === "student" ? "شما" : msg.sender === "teacher" ? "استاد" : "پشتیبان",
      attachment: msg.attachment || null
    });
    setTimeout(() => {
      document.querySelector('.message-input')?.focus();
    }, 100);
  };

  const cancelReply = () => {
    setReplyTo(null);
  };

  // ========== تابع ارسال پیام اصلاح شده ==========
  const handleSendMessage = async () => {
    if (!newMessage.trim() && !selectedFile) {
      showErrorMessage("لطفاً متن پیام را وارد کنید یا یک فایل انتخاب کنید");
      document.querySelector('.message-input')?.focus();
      return;
    }

    const originalMessage = newMessage.trim();
    
    let messageToSend = originalMessage;
    if (!messageToSend && selectedFile) {
      messageToSend = " ";
    }

    setSending(true);
    clearError();
    
    const formData = new FormData();
    if (messageToSend) formData.append("message", messageToSend);
    if (selectedFile) formData.append("attachment", selectedFile);
    
    try {
      let ticketId = ticket?.ticket_id || id;
      
      // اگر ticketId وجود نداشت، اول تیکت رو ایجاد کن با پیام اول
      if (!ticketId) {
        console.log("تیکت وجود ندارد، ایجاد تیکت جدید با پیام اول...");
        const newTicketId = await createNewTicket(id, originalMessage, selectedFile);
        if (newTicketId) {
          ticketId = newTicketId;
          // بعد از ایجاد تیکت، دوباره پیام‌ها رو دریافت کن
          await fetchTicketDetail(ticketId);
          // به روز رسانی URL
          window.history.replaceState(null, '', `/messages-detail/${ticketId}`);
          setNewMessage("");
          setSelectedFile(null);
          if (fileInputRef.current) fileInputRef.current.value = "";
          setSending(false);
          return;
        } else {
          showErrorMessage("خطا در ایجاد تیکت. لطفاً دوباره تلاش کنید.");
          setSending(false);
          return;
        }
      }

      // ارسال پیام با ticketId موجود
      let url;
      let response;

      if (replyTo) {
        url = `/pre_student/api/v1/tickets/${ticketId}/reply/${replyTo.id}/`;
        response = await api.post(url, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        url = `/pre_student/api/v1/tickets-send-message/${ticketId}/`;
        response = await api.post(url, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      if (response.status === 200 || response.status === 201) {
        const newMsg = response.data.sent_message || response.data.message || response.data;
        
        let repliedMessage = null;
        if (replyTo) {
          repliedMessage = {
            id: replyTo.id,
            message: replyTo.message,
            sender: replyTo.sender === "شما" ? "student" : "teacher"
          };
        } else if (newMsg.reply_to_id) {
          const foundMsg = getRepliedMessage(newMsg.reply_to_id);
          if (foundMsg) {
            repliedMessage = {
              id: foundMsg.id,
              message: foundMsg.message === " " || !foundMsg.message ? "[فایل پیوست]" : foundMsg.message,
              sender: foundMsg.sender
            };
          }
        }
        
        let messageText = newMsg.message || newMsg.text;
        
        if (!messageText || messageText === null || messageText === undefined) {
          messageText = originalMessage;
        }
        
        if (messageText === " " && selectedFile && !originalMessage) {
          messageText = "";
        }
        
        const formattedMsg = {
          id: newMsg.id || Date.now(),
          message: messageText,
          sender: newMsg.sender || "student",
          created_at: newMsg.created_at || new Date().toISOString(),
          attachment: newMsg.attachment || null,
          reply_to: repliedMessage,
          is_reply: !!replyTo || newMsg.is_reply || false,
          exercise_title: newMsg.exercise_title || null,
          is_exercise_answer: newMsg.is_exercise_answer || false,
          is_approved: newMsg.is_approved || null,
          reply_to_id: newMsg.reply_to_id || null
        };
        
        setMessages((prev) => [...prev, formattedMsg]);
        setNewMessage("");
        setSelectedFile(null);
        setReplyTo(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        
        // اگر تیکت قبلاً بسته بوده و دوباره باز شده، وضعیت رو به‌روز کن
        if (response.data.ticket_status) {
          setTicket(prev => ({ ...prev, status: response.data.ticket_status }));
        }
      }
    } catch (error) {
      console.error("خطا در ارسال پیام:", error);
      
      let errorMessage = extractErrorMessage(error);
      
      if (errorMessage.includes("larger than") || errorMessage.includes("size")) {
        errorMessage = "حجم فایل ارسالی بیش از حد مجاز است. لطفاً فایل کوچک‌تری انتخاب کنید.";
      } else if (errorMessage.includes("format") || errorMessage.includes("extension")) {
        errorMessage = "فرمت فایل ارسالی پشتیبانی نمی‌شود. لطفاً از فرمت‌های مجاز استفاده کنید.";
      } else if (errorMessage.includes("permission") || errorMessage.includes("access")) {
        errorMessage = "شما دسترسی ارسال پیام در این تیکت را ندارید.";
      } else if (errorMessage.includes("closed") || errorMessage.includes("بسته")) {
        errorMessage = "این تیکت بسته شده است و نمی‌توانید پیام ارسال کنید.";
      }
      
      showErrorMessage(`❌ ${errorMessage}`);
    } finally {
      setSending(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        showErrorMessage("حجم فایل انتخابی بیش از حد مجاز (۱۰ مگابایت) است.");
        e.target.value = "";
        return;
      }
      
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf', 'text/plain', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type) && !file.name.match(/\.(jpg|jpeg|png|gif|webp|pdf|txt|doc|docx)$/i)) {
        showErrorMessage("فرمت فایل انتخابی پشتیبانی نمی‌شود. فرمت‌های مجاز: JPG, PNG, GIF, WebP, PDF, TXT, DOC, DOCX");
        e.target.value = "";
        return;
      }
      
      setSelectedFile(file);
    }
    setError("");
  };

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    if (error) {
      clearError();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !sending) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const getFileUrl = (attachmentPath) => {
    if (!attachmentPath) return null;
    if (attachmentPath.startsWith('http')) return attachmentPath;
    if (attachmentPath.startsWith('/')) {
      return `https://api.pythonteek.com${attachmentPath}`;
    }
    return `https://api.pythonteek.com/${attachmentPath}`;
  };

  const getFileName = (path) => {
    if (!path) return "فایل پیوست";
    const name = path.split('/').pop();
    return name || "فایل پیوست";
  };

  if (loading) {
    return (
      <div className="main-chat-page">
        <div className="personal-info">
          <div className="d-flex mx-3 my-2 justify-content-between align-items-center">
            <div className="fs-6 fw-bold text-dark">در حال بارگذاری...</div>
            <div onClick={goBack} style={{ cursor: "pointer" }} className="p-1">
              <svg width="22" height="18" viewBox="0 0 27 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M26.21 9.99978C26.21 10.7581 25.5889 11.3791 24.8305 11.3791H4.69055L10.9317 17.6548C11.4842 18.2072 11.4842 19.0693 10.9317 19.62C10.6907 19.8628 10.3458 20 9.96574 20C9.62087 20 9.24254 19.8628 9.00147 19.5865L0.413073 10.9657C-0.137691 10.4132 -0.137691 9.55115 0.413073 9.0339L9.00147 0.413026C9.55224 -0.137675 10.4144 -0.137675 10.9669 0.413026C11.5177 0.965452 11.5177 1.82754 10.9669 2.37824L4.69055 8.62044H24.8305C25.5889 8.62044 26.21 9.24149 26.21 9.99978Z" fill="#202244" />
              </svg>
            </div>
          </div>
        </div>
        <div className="chat-container">
          <div className="d-flex justify-content-center align-items-center" style={{ height: '70vh' }}>
            <div className="text-center">
              <div className="spinner-border text-success" role="status" style={{ width: '3rem', height: '3rem' }}>
                <span className="visually-hidden">در حال بارگذاری...</span>
              </div>
              <p className="mt-3 text-muted">در حال بارگذاری پیام‌ها...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-chat-page">
      {/* ============ هدر ============ */}
      <div className="personal-info">
        <div className="d-flex mx-3 my-2 justify-content-between align-items-center">
          <div className="fs-6 fw-bold text-dark">
            {ticket?.package_title || "ارسال پیام به مدرس"}
            {ticket?.status === "closed" && (
              <span className="badge bg-secondary ms-2" style={{ fontSize: '0.7rem' }}>
                بسته شده
              </span>
            )}
          </div>
          <div onClick={goBack} style={{ cursor: "pointer" }} className="p-1">
            <svg width="22" height="18" viewBox="0 0 27 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" clipRule="evenodd" d="M26.21 9.99978C26.21 10.7581 25.5889 11.3791 24.8305 11.3791H4.69055L10.9317 17.6548C11.4842 18.2072 11.4842 19.0693 10.9317 19.62C10.6907 19.8628 10.3458 20 9.96574 20C9.62087 20 9.24254 19.8628 9.00147 19.5865L0.413073 10.9657C-0.137691 10.4132 -0.137691 9.55115 0.413073 9.0339L9.00147 0.413026C9.55224 -0.137675 10.4144 -0.137675 10.9669 0.413026C11.5177 0.965452 11.5177 1.82754 10.9669 2.37824L4.69055 8.62044H24.8305C25.5889 8.62044 26.21 9.24149 26.21 9.99978Z" fill="#202244" />
            </svg>
          </div>
        </div>
      </div>

      {/* ============ محتوای چت ============ */}
      <div className="chat-container">
        <div className="messages-list" >
          {messages.length === 0 ? (
            <div className="d-flex justify-content-center align-items-center " >
            <div className="empty-chat-message">
              <div style={{ fontSize: '3rem', marginBottom: '16px' }}>💬</div>
              <p style={{ color: '#7e7e7e', fontSize: '1.1rem' }}>
                هنوز پیامی ارسال نشده است.
              </p>
              <p style={{ color: '#a0a0a0', fontSize: '0.9rem' }}>
                اولین پیام خود را برای مدرس ارسال کنید.
              </p>
             
            </div>
            </div>
          ) : (
            messages.map((msg, idx) => {
              if (!msg || !msg.sender) return null;
              
              const isStudent = msg.sender === "student";
              const hasExerciseTitle = msg.exercise_title && msg.exercise_title.trim() !== "";
              const isHighlighted = highlightedMessageId === msg.id;
              
              let repliedMessage = msg.reply_to;
              if (!repliedMessage && msg.reply_to_id) {
                const foundMsg = getRepliedMessage(msg.reply_to_id);
                if (foundMsg) {
                  repliedMessage = {
                    id: foundMsg.id,
                    message: foundMsg.message === " " || !foundMsg.message ? "[فایل پیوست]" : foundMsg.message,
                    sender: foundMsg.sender
                  };
                }
              }
              
              return (
                <div 
                  key={msg.id || idx} 
                  ref={el => messageRefs.current[msg.id] = el}
                  className={`message-wrapper ${isStudent ? "wrapper-student" : "wrapper-other"} ${isHighlighted ? 'message-highlighted' : ''}`}
                >
                  <div 
                    className={`message-bubble ${isStudent ? "bubble-student" : "bubble-other"}`}
                    onMouseEnter={() => setHoveredMessageId(msg.id)}
                    onMouseLeave={() => setHoveredMessageId(null)}
                  >
                    {hasExerciseTitle && (
                      <div className="exercise-badge">
                        <span className="exercise-badge-icon">📝</span>
                        <span className="exercise-badge-text">{msg.exercise_title}</span>
                      </div>
                    )}

                    {(msg.is_reply || repliedMessage) && repliedMessage && (
                      <div 
                        className="telegram-reply-quote clickable-reply"
                        onClick={() => scrollToMessage(repliedMessage.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="reply-quote-bar"></div>
                        <div className="reply-quote-content">
                          <span className="reply-quote-author">
                            {repliedMessage.sender === "student" ? "شما" : 
                             repliedMessage.sender === "teacher" ? "استاد" : "پشتیبان"}
                          </span>
                          <span className="reply-quote-text">
                            {repliedMessage.message?.trim() ? repliedMessage.message : "📎 فایل پیوست"}
                          </span>
                        </div>
                      </div>
                    )}

                    {msg.attachment && (
                      <div className="message-attachment">
                        <a
                          href={getFileUrl(msg.attachment)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="file-link"
                        >
                          <span className="file-icon">📎</span>
                          <span className="file-name">{getFileName(msg.attachment)}</span>
                        </a>
                      </div>
                    )}

                    {msg.message && msg.message.trim() !== "" && (
                      <div className="message-text">
                        {msg.message}
                      </div>
                    )}
                    
                    <div className="message-footer">
                      <button
                        className={`reply-btn ${hoveredMessageId === msg.id ? 'visible' : ''}`}
                        onClick={() => handleReply(msg)}
                        title="پاسخ"
                        type="button"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 10H13C15 10 17 12 17 14V20" />
                          <path d="M7 6L3 10L7 14" />
                        </svg>
                      </button>
                      <span className="message-time">
                        {formatTime(msg.created_at)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* ============ نمایش خطا ============ */}
        {error && (
          <div className={`error-container ${showError ? 'error-visible' : 'error-hidden'}`}>
            <div className="error-content">
              <div className="error-icon-wrapper">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="#DC2626" strokeWidth="2"/>
                  <line x1="12" y1="8" x2="12" y2="12" stroke="#DC2626" strokeWidth="2" strokeLinecap="round"/>
                  <circle cx="12" cy="16" r="1" fill="#DC2626"/>
                </svg>
              </div>
              <span className="error-text">{error}</span>
              <button 
                className="error-close-btn"
                onClick={clearError}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* ============ باکس ریپلای شناور ============ */}
        {replyTo && (
          <div className="reply-input-box">
            <div className="reply-input-content">
              <div className="reply-input-label">
                پاسخ به {replyTo.sender}
              </div>
              <div className="reply-input-message">
                {replyTo.message}
              </div>
            </div>
            <button className="reply-input-close" onClick={cancelReply}>
              ✕
            </button>
          </div>
        )}

        {/* ============ فایل انتخاب شده ============ */}
        {selectedFile && (
          <div className="selected-file-box">
            <span className="selected-file-name">📄 {selectedFile.name}</span>
            <button onClick={() => { setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; clearError(); }}>
              ✕
            </button>
          </div>
        )}

        {/* ============ باکس ورودی ============ */}
        <div className="input-container">
          <button className="attach-button" onClick={() => fileInputRef.current?.click()} type="button">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
            </svg>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: "none" }}
              disabled={sending}
            />
          </button>

          <input
            className={`message-input ${error ? 'input-error' : ''}`}
            placeholder="پیام دهید..."
            value={newMessage}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            disabled={sending}
          />

          <button className="send-button" onClick={handleSendMessage} disabled={sending} type="button">
            {sending ? (
              <div className="spinner-border spinner-border-sm text-success" role="status" />
            ) : (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2BA769" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MessagesDetail;